/**
 * Browser-only download helpers for the Utilities QR card (docs/356-ADMIN-UTILITIES-
 * PAGE.md v0's QR slice). Kept out of `@equipped/core` deliberately — everything here
 * touches real DOM APIs (`document`, `<canvas>`, `Blob`, `URL.createObjectURL`), so it's
 * build-verified (`tsc`/`vite build`), not unit-tested under the coverage-gated logic
 * suite (docs/356 §10's own "Admin (Vite/tsc build-verified, not in Jest roots)" note) —
 * the actual QR pixel math it draws from lives in the pure, tested
 * `@equipped/core/qr/{qrMatrix,qrSvg}`.
 */
import { qrSvgDimension, type QrSvgOptions } from "@equipped/core/qr/qrSvg";

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Give the click a tick to start the download before revoking the object URL.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** Download the given SVG markup string as a standalone `.svg` file. */
export function downloadQrSvg(svgMarkup: string, filename: string): void {
  triggerDownload(new Blob([svgMarkup], { type: "image/svg+xml" }), filename);
}

/**
 * Rasterize a `qrMatrix` boolean grid straight to a PNG `Blob` via an offscreen
 * `<canvas>` (never round-tripping through the SVG string) and trigger a download —
 * the same module-run merging isn't needed here since `fillRect` per dark module is
 * cheap at QR sizes and canvas has no per-node cost the SVG string avoids.
 */
export function downloadQrPng(matrix: boolean[][], filename: string, opts?: QrSvgOptions): Promise<void> {
  const moduleSize = Math.max(1, opts?.moduleSize ?? 8);
  const quietZone = Math.max(0, opts?.quietZone ?? 4);
  const foreground = opts?.foreground ?? "#000000";
  const background = opts?.background ?? "#ffffff";
  const dim = qrSvgDimension(matrix.length, { moduleSize, quietZone });

  const canvas = document.createElement("canvas");
  canvas.width = dim;
  canvas.height = dim;
  const ctx = canvas.getContext("2d");
  if (!ctx) return Promise.reject(new Error("Canvas 2D context unavailable"));

  ctx.fillStyle = background;
  ctx.fillRect(0, 0, dim, dim);
  ctx.fillStyle = foreground;
  for (let y = 0; y < matrix.length; y++) {
    const row = matrix[y];
    if (!row) continue;
    for (let x = 0; x < row.length; x++) {
      if (row[x]) ctx.fillRect((x + quietZone) * moduleSize, (y + quietZone) * moduleSize, moduleSize, moduleSize);
    }
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("Could not encode PNG"));
        return;
      }
      triggerDownload(blob, filename);
      resolve();
    }, "image/png");
  });
}
